import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  RefreshCw,
  Eye,
  AlertCircle,
  History,
  Mail,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const ExpiryReminderHistory = () => {
  usePageTitle('Reminder History');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 15;

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/expiry-reminders/history?page=${page}&page_size=${pageSize}`);
      setHistory(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch history:', error);
      toast.error('Failed to load reminder history');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  // Access check
  if (!['admin', 'training_admin'].includes(user?.role)) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 mt-2">Only administrators and training admins can access this page.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/expiry-reminders')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Reminder History
            </h1>
            <p className="text-slate-500 text-sm">View all previously sent expiry reminder emails</p>
          </div>
        </div>
        <Button variant="outline" onClick={fetchHistory} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* History Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <History className="w-5 h-5 text-[#2C9DE1]" />
            Sent Reminders
          </CardTitle>
          <CardDescription>
            {total} reminder{total !== 1 ? 's' : ''} sent in total
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#2C9DE1]" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No reminders have been sent yet</p>
              <Button 
                onClick={() => navigate('/expiry-reminders')} 
                className="mt-4 bg-[#2C9DE1] hover:bg-[#1A7BB8]"
              >
                Go to Expiry Reminders
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date & Time</TableHead>
                    <TableHead>Company</TableHead>
                    <TableHead>Sent To</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-center">Certificates</TableHead>
                    <TableHead>Sent By</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-sm">
                        {formatDateTime(item.sent_at)}
                      </TableCell>
                      <TableCell className="font-medium">
                        {item.company_name}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600">
                        {item.recipient_email}
                      </TableCell>
                      <TableCell>
                        <Badge className={item.expiry_window === -1 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                          {item.expiry_window === -1 ? 'Expired' : `${item.expiry_window} days`}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline">{item.certificate_count}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500">
                        {item.sent_by}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/expiry-reminder-history/${item.id}`)}
                          className="text-[#2C9DE1] hover:text-[#1A7BB8]"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <div className="text-sm text-slate-500">
                    Page {page} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ExpiryReminderHistory;
